import Vue from 'vue'
import VueI18n from 'vue-i18n'
import merge from 'lodash.merge'
import { Decimal } from 'decimal.js'
import WAValidator from 'multicoin-address-validator'

import { extend, configure } from 'vee-validate'
import zhValidate from 'vee-validate/dist/locale/zh_CN.json'
import enValidate from 'vee-validate/dist/locale/en.json'
import jaValidate from 'vee-validate/dist/locale/ja.json'
import koValidate from 'vee-validate/dist/locale/ko.json'
import ptValidate from 'vee-validate/dist/locale/pt_PT.json'
import esValidate from 'vee-validate/dist/locale/es.json'

import zh from '../locales/zh.json'
import en from '../locales/en.json'
import ja from '../locales/ja.json'
import ko from '../locales/ko.json'
import pt from '../locales/pt.json'
import es from '../locales/es.json'

import dayjs from './dayjs'

const _zh = require(`../${process.env.TEMPLATE_ID}/locales/zh.json`)
const _en = require(`../${process.env.TEMPLATE_ID}/locales/en.json`)
const _ja = require(`../${process.env.TEMPLATE_ID}/locales/ja.json`)
const _ko = require(`../${process.env.TEMPLATE_ID}/locales/ko.json`)
const _pt = require(`../${process.env.TEMPLATE_ID}/locales/pt.json`)
const _es = require(`../${process.env.TEMPLATE_ID}/locales/es.json`)

Vue.use(VueI18n)

export default ({ app, store }) => {
  // Set i18n instance on app
  // This way we can use it in middleware and pages asyncData/fetch
  app.i18n = new VueI18n({
    locale: store.state.locale,
    fallbackLocale: process.env.DEFAULT_LOCALE || 'zh',
    messages: {
      zh: merge(zh, _zh, {
        validate: {
          ...zhValidate.messages,
          is: '{_field_}不一致',
          is_not: '{_field_}无效'
        }
      }),
      en: merge(en, _en, {
        validate: {
          ...enValidate.messages,
          is: '{_field_} is different',
          is_not: '{_field_} is invalid'
        }
      }),
      ja: merge(ja, _ja, {
        validate: {
          ...jaValidate.messages
        }
      }),
      ko: merge(ko, _ko, {
        validate: {
          ...koValidate.messages,
          is: '{_field_}不一致',
          is_not: '{_field_}无效'
        }
      }),
      pt: merge(pt, _pt, {
        validate: {
          ...ptValidate.messages,
          is: '{_field_} is different',
          is_not: '{_field_} is invalid'
        }
      }),
      es: merge(es, _es, {
        validate: {
          ...esValidate.messages,
          is: '{_field_} is different',
          is_not: '{_field_} is invalid'
        }
      })
    }
  })

  configure({
    defaultMessage: (field, values) => {
      const nameKey = `validate.fields.${field}.name`
      const ruleKey = `validate.fields.${field}.${values._rule_}`
      if (app.i18n.te(nameKey)) values._field_ = app.i18n.t(nameKey)
      if (app.i18n.te(ruleKey)) return app.i18n.t(ruleKey, values)
      return app.i18n.t(`validate.${values._rule_}`, values)
    }
  })

  extend('tel_len', {
    validate: (value, { length } = {}) => Number(length) === value.length,
    params: ['length'],
    message: (fieldName, { length } = {}) =>
      app.i18n.t('validate.tel_len', [fieldName, length])
  })

  extend('wechat', {
    validate: value =>
      new RegExp(/^1\d{10}$/).test(value) ||
      new RegExp(/^[a-zA-Z][-_a-zA-Z0-9]{5,19}$/).test(value),
    message: () => app.i18n.t('validate.wechat')
  })

  extend('unit', {
    // value 必須為 unit 的倍數 (餘數為 0)
    validate: (value, { unit }) => {
      const numValue = new Decimal(value)
      const numUnit = new Decimal(unit)

      if (numValue.isNaN() || numUnit.isNaN() || numUnit.isZero()) {
        return false
      }

      return numValue.mod(numUnit).isZero()
    },
    params: ['unit'],
    message: (fieldName, { unit } = {}) =>
      app.i18n.t('validate.unit', [fieldName, unit])
  })

  extend('price', {
    validate: value => new RegExp(/^(\d+)(\.(\d+))?$/g).test(value),
    message: fieldName => app.i18n.t('validate.format', [fieldName])
  })

  extend('no_number', {
    validate: value => new RegExp(/^[^0-9]*$/).test(value),
    message: fieldName => app.i18n.t('validate.no_number', [fieldName])
  })

  extend('wallet', {
    validate: (value, { currency }) => {
      currency = currency.toLowerCase()
      // 其實 WAValidator 有 opts (chainType) 可以自動轉到 btc 與 eth 驗證，但沒公開接口，故一樣直接轉 currency 來驗證
      if (currency === 'usdt-omni') {
        currency = 'btc'
      } else if (currency === 'usdt-erc20') {
        currency = 'eth'
      }

      // @deprecated customize
      // if (currency === 'btc') {
      //   return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/gm.test(value)
      // }
      // if (currency === 'ltc') {
      //   return /^(ltc1|[LM3])[a-zA-HJ-NP-Z0-9]{26,40}$/gm.test(value)
      // }

      // original
      try {
        // 若為 bch 也會同時驗證是否為 btc，兩者其一驗證成功就算成功 (https://trello.com/c/fF0K9HM2)
        if (currency === 'bch') {
          return (
            WAValidator.validate(value, currency) ||
            WAValidator.validate(value, 'btc')
          )
        }
        return WAValidator.validate(value, currency)
      } catch (err) {
        console.error(err.message)
      }
      return true
    },
    params: ['currency'],
    message: () => app.i18n.t('validate.wallet')
  })

  extend('alpha_and_num', {
    validate: value =>
      new RegExp(/^(?:[0-9]+[a-z]|[a-z]+[0-9])[a-z0-9]*$/i).test(value),
    message: fieldName => app.i18n.t('validate.alpha_and_num', [fieldName])
  })

  extend('date', {
    validate: value => new RegExp(/(\d{4})-(\d{2})-(\d{2})/).test(value),
    message: fieldName => app.i18n.t('validate.date', [fieldName])
  })

  extend('date_after_year', {
    validate: (value, { year }) =>
      dayjs()
        .subtract(year, 'year')
        .isAfter(dayjs(value, 'YYYY-MM-DD')),
    params: ['year'],
    message: () => app.i18n.t('validate.oneOf') // 等有需要顯示文字再設置特定翻譯
  })
}
